import { useState } from "react";
import { Copy, Download, CheckCircle2 } from "lucide-react";

// Simple internal kit page — QR code + copy-ready email signature HTML for Andrew/Mark.
// Not linked from the public nav; share the /kit URL directly.

const PRODUCTION_URL = "https://cozyworkspaces.com.au";
const QR_BRAND_URL = "/kit/qr-workspaces.png";
const QR_PLAIN_URL = "/kit/qr-workspaces-plain.png";
const QR_SIGNATURE_URL = "/kit/qr-workspaces-signature.png";

// The signature HTML — deliberately simple (Outlook strips fancy CSS).
// Absolute image src so Outlook always resolves it from the live site.
const buildSignatureHtml = (name, role, phone) => `<table cellpadding="0" cellspacing="0" style="font-family: Georgia, 'Times New Roman', serif; color: #2D231E; line-height: 1.4;">
  <tr>
    <td style="padding-right: 18px; border-right: 1px solid #E6DFD3; vertical-align: top;">
      <a href="${PRODUCTION_URL}" style="text-decoration: none; color: #2D231E;">
        <img src="${PRODUCTION_URL}${QR_SIGNATURE_URL}" width="110" height="110" alt="Scan to visit WorkSpaces" style="display:block; border:0;" />
      </a>
    </td>
    <td style="padding-left: 18px; vertical-align: top;">
      <p style="margin:0 0 4px; font-size: 18px; font-style: italic;">${name || "Andrew Willocks"}</p>
      <p style="margin:0 0 10px; font-family: Arial, sans-serif; font-size: 11px; letter-spacing: 2px; text-transform: uppercase; color: #8B7355;">${role || "WorkSpaces"}</p>
      <p style="margin:0 0 2px; font-family: Arial, sans-serif; font-size: 12px; color: #5D4037;">Saba · Toowoomba &nbsp;·&nbsp; Buderim · Sunshine Coast</p>
      <p style="margin:0 0 2px; font-family: Arial, sans-serif; font-size: 12px; color: #5D4037;">${phone || "(07) — ——— ————"}</p>
      <p style="margin:0; font-family: Arial, sans-serif; font-size: 12px;">
        <a href="${PRODUCTION_URL}" style="color: #4A3C31; text-decoration: none;">${PRODUCTION_URL.replace(/^https?:\/\//, "")}</a>
      </p>
      <p style="margin:8px 0 0; font-family: Arial, sans-serif; font-size: 10px; letter-spacing: 1.5px; text-transform: uppercase; color: #8B7355;">Boardrooms · Hot Desks · Tenant Leasing</p>
    </td>
  </tr>
</table>`;

const CopyButton = ({ text, label = "Copy" }) => {
  const [copied, setCopied] = useState(false);
  const handle = async () => {
    try { await navigator.clipboard.writeText(text); setCopied(true); setTimeout(() => setCopied(false), 1800); }
    catch { setCopied(false); }
  };
  return (
    <button
      type="button"
      onClick={handle}
      data-testid="copy-button"
      className="inline-flex items-center gap-2 px-4 py-2 bg-[#4A3C31] text-[#F9F6F0] font-sans text-xs tracking-[0.25em] uppercase rounded-sm hover:bg-[#2D231E] transition-colors"
    >
      {copied ? <CheckCircle2 size={14} strokeWidth={1.5} /> : <Copy size={14} strokeWidth={1.5} />}
      {copied ? "Copied" : label}
    </button>
  );
};

export default function KitPage() {
  const [name, setName] = useState("Andrew Willocks");
  const [role, setRole] = useState("WorkSpaces");
  const [phone, setPhone] = useState("");
  const signature = buildSignatureHtml(name, role, phone);

  return (
    <main data-testid="kit-page" className="min-h-screen bg-[#F9F6F0] py-16 sm:py-24 px-6 sm:px-12">
      <div className="max-w-5xl mx-auto">
        <p className="font-sans text-xs tracking-[0.35em] uppercase text-[#8B7355] mb-3">Internal — brand kit</p>
        <h1 className="font-serif text-4xl sm:text-5xl text-[#2D231E] mb-4">
          Scan &amp; share.
        </h1>
        <p className="font-sans text-base text-[#5D4037] max-w-2xl mb-12">
          A QR code and an Outlook-ready email signature — for you and Mark to
          drop into everyday correspondence while advertising ramps up.
        </p>

        {/* QR SECTION */}
        <section className="border border-[#E6DFD3] bg-[#FAF9F6] rounded-sm p-8 sm:p-10 mb-12">
          <h2 className="font-serif text-2xl text-[#2D231E] mb-1">QR code</h2>
          <p className="font-sans text-sm text-[#5D4037] mb-8">
            Scans directly to the live site. Print on flyers, business cards, or drop into slide decks.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="text-center">
              <img src={QR_BRAND_URL} alt="Brand QR code" className="w-56 h-56 mx-auto border border-[#E6DFD3] rounded-sm" data-testid="qr-brand" />
              <p className="mt-3 font-sans text-[10px] tracking-[0.25em] uppercase text-[#8B7355]">Brand · cream + espresso</p>
              <a href={QR_BRAND_URL} download className="inline-flex items-center gap-2 mt-4 px-4 py-2 border border-[#4A3C31] text-[#4A3C31] font-sans text-xs tracking-[0.25em] uppercase rounded-sm hover:bg-[#4A3C31] hover:text-[#F9F6F0] transition-colors">
                <Download size={14} strokeWidth={1.5} /> Download PNG
              </a>
            </div>

            <div className="text-center">
              <img src={QR_PLAIN_URL} alt="Plain QR code" className="w-56 h-56 mx-auto border border-[#E6DFD3] rounded-sm bg-white" data-testid="qr-plain" />
              <p className="mt-3 font-sans text-[10px] tracking-[0.25em] uppercase text-[#8B7355]">Print · black on white</p>
              <a href={QR_PLAIN_URL} download className="inline-flex items-center gap-2 mt-4 px-4 py-2 border border-[#4A3C31] text-[#4A3C31] font-sans text-xs tracking-[0.25em] uppercase rounded-sm hover:bg-[#4A3C31] hover:text-[#F9F6F0] transition-colors">
                <Download size={14} strokeWidth={1.5} /> Download PNG
              </a>
            </div>
          </div>
        </section>

        {/* SIGNATURE SECTION */}
        <section className="border border-[#E6DFD3] bg-[#FAF9F6] rounded-sm p-8 sm:p-10 mb-12">
          <h2 className="font-serif text-2xl text-[#2D231E] mb-1">Outlook email signature</h2>
          <p className="font-sans text-sm text-[#5D4037] mb-6">
            Fill in the fields, click Copy, then paste into <em>Outlook → File → Options → Mail → Signatures</em>.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <label className="block">
              <span className="block font-sans text-[10px] tracking-[0.25em] uppercase text-[#5D4037] mb-1.5">Name</span>
              <input value={name} onChange={(e) => setName(e.target.value)} data-testid="sig-name" className="w-full bg-white border border-[#E6DFD3] px-3 py-2 font-sans text-sm text-[#2D231E] focus:outline-none focus:border-[#4A3C31]" />
            </label>
            <label className="block">
              <span className="block font-sans text-[10px] tracking-[0.25em] uppercase text-[#5D4037] mb-1.5">Role / Business</span>
              <input value={role} onChange={(e) => setRole(e.target.value)} data-testid="sig-role" className="w-full bg-white border border-[#E6DFD3] px-3 py-2 font-sans text-sm text-[#2D231E] focus:outline-none focus:border-[#4A3C31]" />
            </label>
            <label className="block">
              <span className="block font-sans text-[10px] tracking-[0.25em] uppercase text-[#5D4037] mb-1.5">Phone</span>
              <input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="(07) — —" data-testid="sig-phone" className="w-full bg-white border border-[#E6DFD3] px-3 py-2 font-sans text-sm text-[#2D231E] focus:outline-none focus:border-[#4A3C31]" />
            </label>
          </div>

          {/* Live preview */}
          <div className="border border-[#E6DFD3] bg-white p-6 rounded-sm mb-4">
            <p className="font-sans text-[10px] tracking-[0.25em] uppercase text-[#8B7355] mb-4">Preview</p>
            <div data-testid="sig-preview" dangerouslySetInnerHTML={{ __html: signature }} />
          </div>

          <div className="flex flex-wrap gap-3 items-center">
            <CopyButton text={signature} label="Copy HTML" />
            <a href={PRODUCTION_URL} target="_blank" rel="noreferrer" className="font-sans text-xs tracking-[0.25em] uppercase text-[#8B7355] hover:text-[#4A3C31]">
              Open live site ↗
            </a>
          </div>

          <details className="mt-6">
            <summary className="cursor-pointer font-sans text-xs tracking-[0.25em] uppercase text-[#8B7355]">
              How to paste into Outlook (3 steps)
            </summary>
            <ol className="mt-3 pl-5 list-decimal font-sans text-sm text-[#5D4037] space-y-1.5">
              <li>Open Outlook (desktop). <em>File</em> → <em>Options</em> → <em>Mail</em> → <em>Signatures…</em></li>
              <li>Click <em>New</em>, name it &ldquo;WorkSpaces&rdquo;, then click into the signature editing box.</li>
              <li>Right-click → <em>Paste</em>. The QR + text will appear. Save. Assign to New Messages + Replies/Forwards.</li>
            </ol>
          </details>
        </section>

        {/* FLYERS DOWNLOAD */}
        <section className="border border-[#E6DFD3] bg-[#FAF9F6] rounded-sm p-8 sm:p-10 mb-12">
          <h2 className="font-serif text-2xl text-[#2D231E] mb-1">One-page flyers</h2>
          <p className="font-sans text-sm text-[#5D4037] mb-6">
            Three print-ready A4 flyers, one per service. Warm, on-brand,
            editable in Word. Perfect for reception, tours, or emailing a
            single-page teaser to a specific enquiry.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <a
              href="/kit/WorkSpaces_Flyer_Leasing.docx"
              download
              data-testid="download-flyer-leasing"
              className="group flex flex-col items-start p-5 bg-[#F9F6F0] border border-[#E6DFD3] rounded-sm hover:bg-[#4A3C31] transition-colors"
            >
              <p className="font-sans text-[10px] tracking-[0.25em] uppercase text-[#8B7355] group-hover:text-[#C4A484] mb-1">Flyer 01</p>
              <p className="font-serif text-lg text-[#2D231E] group-hover:text-[#F9F6F0] leading-tight mb-3">Tenant Leasing</p>
              <span className="inline-flex items-center gap-1.5 mt-auto font-sans text-[10px] tracking-[0.25em] uppercase text-[#4A3C31] group-hover:text-[#F9F6F0]">
                <Download size={12} strokeWidth={1.5} /> Download .docx
              </span>
            </a>
            <a
              href="/kit/WorkSpaces_Flyer_HotDesking.docx"
              download
              data-testid="download-flyer-hotdesk"
              className="group flex flex-col items-start p-5 bg-[#F9F6F0] border border-[#E6DFD3] rounded-sm hover:bg-[#4A3C31] transition-colors"
            >
              <p className="font-sans text-[10px] tracking-[0.25em] uppercase text-[#8B7355] group-hover:text-[#C4A484] mb-1">Flyer 02</p>
              <p className="font-serif text-lg text-[#2D231E] group-hover:text-[#F9F6F0] leading-tight mb-3">Hot Desking</p>
              <span className="inline-flex items-center gap-1.5 mt-auto font-sans text-[10px] tracking-[0.25em] uppercase text-[#4A3C31] group-hover:text-[#F9F6F0]">
                <Download size={12} strokeWidth={1.5} /> Download .docx
              </span>
            </a>
            <a
              href="/kit/WorkSpaces_Flyer_BoardroomHire.docx"
              download
              data-testid="download-flyer-boardroom"
              className="group flex flex-col items-start p-5 bg-[#F9F6F0] border border-[#E6DFD3] rounded-sm hover:bg-[#4A3C31] transition-colors"
            >
              <p className="font-sans text-[10px] tracking-[0.25em] uppercase text-[#8B7355] group-hover:text-[#C4A484] mb-1">Flyer 03</p>
              <p className="font-serif text-lg text-[#2D231E] group-hover:text-[#F9F6F0] leading-tight mb-3">Boardroom Hire</p>
              <span className="inline-flex items-center gap-1.5 mt-auto font-sans text-[10px] tracking-[0.25em] uppercase text-[#4A3C31] group-hover:text-[#F9F6F0]">
                <Download size={12} strokeWidth={1.5} /> Download .docx
              </span>
            </a>
          </div>
        </section>

        {/* MEDIA KIT DOWNLOAD */}
        <section className="border border-[#E6DFD3] bg-[#FAF9F6] rounded-sm p-8 sm:p-10 mb-12">
          <h2 className="font-serif text-2xl text-[#2D231E] mb-1">Client brochure (.docx)</h2>
          <p className="font-sans text-sm text-[#5D4037] mb-6">
            A four-page, brand-styled Word document for potential leasing
            clients — leasing tenancies + pricing table, hot desks, boardroom
            hire, and a contact page with website + Instagram QR codes.
            Editable if you need to tweak names or pricing.
          </p>
          <a
            href="/kit/WorkSpaces_Client_Brochure.docx"
            download="WorkSpaces_Client_Brochure.docx"
            data-testid="download-brochure"
            className="inline-flex items-center gap-2 px-5 py-3 bg-[#4A3C31] text-[#F9F6F0] font-sans text-xs tracking-[0.25em] uppercase rounded-sm hover:bg-[#2D231E] transition-colors"
          >
            <Download size={14} strokeWidth={1.5} />
            Download Brochure (.docx)
          </a>
          <p className="mt-3 font-sans text-[10px] tracking-[0.2em] uppercase text-[#8B7355]">
            4 pages · 3 MB · leasing · hot desking · boardroom hire · contact
          </p>
        </section>

        {/* SOCIAL IMAGE PACK */}
        <section className="border border-[#E6DFD3] bg-[#FAF9F6] rounded-sm p-8 sm:p-10">
          <h2 className="font-serif text-2xl text-[#2D231E] mb-1">Social media image pack</h2>
          <p className="font-sans text-sm text-[#5D4037] mb-6">
            One zip with every image from the site, organised by section
            (hero, boardroom, hot desks, leasing, sketches, QR codes) and
            filenamed so your team can drop them straight into posts. Includes
            a short README with usage tips.
          </p>
          <a
            href="/kit/workspaces-media-kit.zip"
            download="WorkSpaces_Media_Kit.zip"
            data-testid="download-media-kit"
            className="inline-flex items-center gap-2 px-5 py-3 bg-[#4A3C31] text-[#F9F6F0] font-sans text-xs tracking-[0.25em] uppercase rounded-sm hover:bg-[#2D231E] transition-colors"
          >
            <Download size={14} strokeWidth={1.5} />
            Download Media Kit (.zip)
          </a>
          <p className="mt-3 font-sans text-[10px] tracking-[0.2em] uppercase text-[#8B7355]">
            21 images · 9.1 MB · organised in 8 folders
          </p>
        </section>

        <p className="mt-10 font-sans text-xs text-[#8B7355]">
          The QR resolves to <code>{PRODUCTION_URL}</code>. When workspaces.au goes live,
          this page will auto-update — no re-generation needed.
        </p>
      </div>
    </main>
  );
}
